import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarDays, CalendarOff, ChevronLeft, ChevronRight, Clock } from 'lucide-react';

import { ThemeToggle } from '@/components/ThemeToggle';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { useApiQuery } from '@/hooks/useApiQuery';
import type { TimetableSlot } from '@/types/timetable';
import { timetableApi } from '../api/timetable.api';
import { AvailabilityModal } from '../components/timetable/AvailabilityModal';
import { SlotCard } from '../components/timetable/SlotCard';
import { WEEKDAY_LABEL, periodRange, periodTitle } from '../components/timetable/timetableCopy';
import { formatInstituteDate } from '../utils';

/**
 * One day of the teacher's own timetable, period by period.
 *
 * The same shape as the homework review and the lesson page: a back link rather
 * than a second copy of the console's navigation, and the same components the
 * week grid uses — a slot is drawn by `SlotCard` and edited through
 * `AvailabilityModal` here exactly as it is there (CLAUDE.md §3).
 *
 * Free periods are shown, not hidden. A teacher opening a day wants to know
 * where the gaps are as much as where the lessons are.
 */

export function TeacherTimetableDayPage() {
  const { date } = useParams<{ date: string }>();
  const navigate = useNavigate();
  const [editing, setEditing] = useState<TimetableSlot | null>(null);

  const { data, isLoading, error, refetch } = useApiQuery(() => timetableApi.day(date), [date]);

  function goToDay(step: number) {
    if (!data) return;
    const next = new Date(`${data.date}T00:00:00.000Z`);
    next.setUTCDate(next.getUTCDate() + step);
    navigate(`/teacher/timetable/${next.toISOString().slice(0, 10)}`);
  }

  // A day already past can be read but not changed — the register for it has
  // been taken, and the server refuses the write regardless. The server says
  // which days are past, on the school's calendar rather than this device's.
  const isLocked = Boolean(data?.isPast);

  return (
    <div className="min-h-dvh bg-paper">
      <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-line bg-surface px-4 py-3 lg:px-6">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<ArrowLeft className="h-4 w-4" />}
          onClick={() => navigate('/teacher/timetable')}
        >
          Back to my week
        </Button>
        <ThemeToggle />
      </header>

      <main className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-6 lg:px-6 lg:py-8">
        {isLoading && (
          <div className="card p-5">
            <SkeletonLines count={7} />
          </div>
        )}

        {!isLoading && error && (
          <div className="card flex flex-col items-start gap-3 p-5">
            <Alert tone="critical" title="Could not load this day">
              {error.message}
            </Alert>
            <Button variant="secondary" size="sm" onClick={refetch}>
              Try again
            </Button>
          </div>
        )}

        {!isLoading && !error && data && (
          <>
            {/* --- the day ------------------------------------------------ */}
            <div className="card flex flex-wrap items-center justify-between gap-3 p-5">
              <div className="min-w-0">
                <p className="lbl">{WEEKDAY_LABEL[data.weekday]}</p>
                <h1 className="mt-1 flex items-center gap-2 text-2xl">
                  {formatInstituteDate(data.date)}
                  {data.isToday && <Badge tone="accent">Today</Badge>}
                  {data.holiday && <Badge tone="info">Holiday</Badge>}
                </h1>
                <p className="mt-1 text-sm text-ink-2">
                  {data.holiday
                    ? data.holiday.name
                    : data.summary.periods === 0
                      ? 'Nothing scheduled'
                      : `${data.summary.periods} periods`}
                  {!data.holiday &&
                    data.summary.unavailable > 0 &&
                    ` · ${data.summary.unavailable} marked away`}
                </p>
              </div>

              <div className="flex shrink-0 items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Previous day"
                  leftIcon={<ChevronLeft className="h-4 w-4" />}
                  onClick={() => goToDay(-1)}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Next day"
                  leftIcon={<ChevronRight className="h-4 w-4" />}
                  onClick={() => goToDay(1)}
                />
              </div>
            </div>

            {/* A declared closure outranks the recurring week. The entries repeat
                all year and know nothing about the calendar, so a day the office
                has closed would otherwise still list its lessons. */}
            {data.holiday ? (
              <div className="card">
                <EmptyState
                  icon={<CalendarOff className="h-5 w-5" />}
                  title={`${data.holiday.name} — your institute is closed`}
                  description={
                    data.holiday.isStaffWorking
                      ? 'No classes are taught today. Staff are still expected in, so check with the office about what is on.'
                      : 'No classes are taught today, and nobody is marked absent for it.'
                  }
                />
              </div>
            ) : data.periods.length === 0 ? (
              <div className="card">
                <EmptyState
                  icon={<Clock className="h-5 w-5" />}
                  title="Your institute has not set up its periods yet"
                  description="Once the office defines when each period runs, your day appears here."
                />
              </div>
            ) : data.summary.periods === 0 ? (
              <div className="card">
                <EmptyState
                  icon={<CalendarDays className="h-5 w-5" />}
                  title="Nothing scheduled on this day"
                  description="You have no periods in the week's timetable for this day."
                />
              </div>
            ) : (
              /* --- period by period, free ones included ------------------ */
              <ul className="flex flex-col gap-3">
                {data.periods.map((period) => {
                  const slot = data.slots.find((item) => item.period.uuid === period.uuid);

                  return (
                    <li key={period.uuid} className="flex items-stretch gap-3">
                      <div className="w-24 shrink-0 pt-1">
                        <p className="text-sm font-[560] text-ink">{periodTitle(period)}</p>
                        <p className="font-mono text-2xs text-ink-3">
                          {periodRange(period.startTime, period.endTime)}
                        </p>
                      </div>

                      <div className="min-w-0 flex-1">
                        {slot ? (
                          <SlotCard
                            slot={slot}
                            variant="day"
                            isLocked={isLocked}
                            onClick={isLocked ? undefined : setEditing}
                          />
                        ) : (
                          <div className="flex h-full items-center rounded-field border border-dashed border-line-strong bg-surface-2/40 px-3.5 py-3">
                            {/* A break is the institute's and applies to everybody; a
                                free period is this teacher's alone. */}
                            <p className="text-sm text-ink-3">
                              {period.kind === 'BREAK' ? 'No class — a break' : 'Free period'}
                            </p>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </main>

      {editing && (
        <AvailabilityModal slot={editing} onClose={() => setEditing(null)} onSaved={refetch} />
      )}
    </div>
  );
}
